import { useState, useEffect } from 'react';
import type { DrinkTemplate } from '../../types';

interface DrinkButtonsProps {
  onAddDrink: (templateId: string) => void;
}

// Fallback quick drinks if API fails
const fallbackDrinks: DrinkTemplate[] = [
  { id: 'beer-10-500', name: 'Pivo 10° 0.5l', category: 'beer', volumeMl: 500, alcoholPercent: 4.0 },
  { id: 'beer-11-500', name: 'Pivo 11° 0.5l', category: 'beer', volumeMl: 500, alcoholPercent: 4.4 },
  { id: 'beer-12-500', name: 'Pivo 12° 0.5l', category: 'beer', volumeMl: 500, alcoholPercent: 5.0 },
  { id: 'wine-200', name: 'Víno 0.2l', category: 'wine', volumeMl: 200, alcoholPercent: 12.0 },
  { id: 'spirit-40-40', name: 'Panák 40% 4cl', category: 'spirit', volumeMl: 40, alcoholPercent: 40.0 },
  { id: 'spirit-40-20', name: 'Panák 40% 2cl', category: 'spirit', volumeMl: 20, alcoholPercent: 40.0 },
];

export default function DrinkButtons({ onAddDrink }: DrinkButtonsProps) {
  const [drinks, setDrinks] = useState<DrinkTemplate[]>(fallbackDrinks);

  useEffect(() => {
    const fetchQuickDrinks = async () => {
      try {
        const apiUrl = import.meta.env.VITE_API_URL || '';
        const response = await fetch(`${apiUrl}/api/drinks/quick`);
        if (response.ok) {
          const data = await response.json();
          if (data.drinks && data.drinks.length > 0) {
            setDrinks(data.drinks);
          }
        }
      } catch {
        // Use fallback drinks
      }
    };

    fetchQuickDrinks();
  }, []);

  const getCategoryIcon = (category: DrinkTemplate['category']) => {
    switch (category) {
      case 'beer':
        return '🍺';
      case 'wine':
        return '🍷';
      case 'spirit':
        return '🥃';
      default:
        return '🍹';
    }
  };

  const getCategoryColor = (category: DrinkTemplate['category']) => {
    switch (category) {
      case 'beer':
        return 'bg-amber-600 hover:bg-amber-500';
      case 'wine':
        return 'bg-purple-600 hover:bg-purple-500';
      case 'spirit':
        return 'bg-orange-600 hover:bg-orange-500';
      default:
        return 'bg-blue-600 hover:bg-blue-500';
    }
  };

  return (
    <div className="grid grid-cols-2 gap-3">
      {drinks.map((drink) => (
        <button
          key={drink.id}
          onClick={() => onAddDrink(drink.id)}
          className={`${getCategoryColor(drink.category)} text-white py-3 px-4 rounded-lg font-medium transition-colors active:scale-95 transform`}
        >
          <div className="flex items-center gap-2">
            <span className="text-xl">{getCategoryIcon(drink.category)}</span>
            <div className="text-left">
              <div className="text-sm font-semibold">{drink.name}</div>
              <div className="text-xs opacity-80">{drink.alcoholPercent}%</div>
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}
